import { api, isApiError } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  companyPipelineDtoSchema,
  pipelineCouplesDtoSchema,
  pipelineEmailDtoSchema,
  venuePipelineDtoSchema,
  type CompanyPipelineDto,
  type PipelineCouplesDto,
  type PipelineEmailDto,
  type VenuePipelineDto,
} from '@lib/dto/pipeline.dto'
import type { PipelineCouplesQuery, PipelineQuery, PutPipelineEmailInput } from '@lib/input/pipeline.input'
import type { IPipelineService } from '@/types/PipelineService.types'

/** The pipeline view (docs/pipeline-view.spec.md). */
class PipelineServiceImpl implements IPipelineService {
  async venue(venueId: string, query: PipelineQuery): Promise<VenuePipelineDto> {
    try {
      return await api.get(`/venues/${venueId}/pipeline${searchOf(query)}`, venuePipelineDtoSchema)
    } catch (error) {
      // A pipeline address for a venue that is gone, was removed, or sits outside
      // the venues this person can open is the page's answer, not a fault. The
      // API looks the venue up in the caller's tenant, among the venues they
      // reach, and leaves out one that was removed; 404 is that lookup coming
      // back empty. A server error, a dropped connection, or a reply this page
      // cannot read is still reported — a missing venue is not one of those.
      if (!(isApiError(error) && error.status === 404)) {
        logRequestFailure('Failed to load the venue’s pipeline', { venueId }, error)
      }
      throw error
    }
  }

  async company(query: PipelineQuery): Promise<CompanyPipelineDto> {
    try {
      return await api.get(`/pipeline/summary${searchOf(query)}`, companyPipelineDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the company’s pipeline', {}, error)
      throw error
    }
  }

  async couples(venueId: string, query: PipelineCouplesQuery): Promise<PipelineCouplesDto> {
    try {
      return await api.get(`/venues/${venueId}/pipeline/couples${searchOf(query)}`, pipelineCouplesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the couples behind a pipeline count', { venueId, target: query.target, list: query.list }, error)
      throw error
    }
  }

  async setEmail(input: PutPipelineEmailInput): Promise<PipelineEmailDto> {
    try {
      return await api.put('/pipeline/email', input, pipelineEmailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to change the pipeline email', {}, error)
      throw error
    }
  }
}

/** A query as a search string: `?view=activity`, or nothing. */
function searchOf(query: Partial<PipelineQuery> & { target?: string | undefined; list?: string | undefined; page?: number | undefined }): string {
  const params = new URLSearchParams()
  if (query.split !== undefined) params.set('split', query.split)
  if (query.view !== undefined) params.set('view', query.view)
  if (query.period !== undefined) params.set('period', query.period)
  if (query.span !== undefined) params.set('span', query.span)
  if (query.compare !== undefined) params.set('compare', query.compare)
  if (query.target !== undefined) params.set('target', query.target)
  if (query.list !== undefined) params.set('list', query.list)
  if (query.page !== undefined) params.set('page', String(query.page))
  const search = params.toString()
  return search === '' ? '' : `?${search}`
}

export const pipelineService: IPipelineService = new PipelineServiceImpl()
